import { useEffect } from "react";
import { App, Button, Divider, Space, Tooltip } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";

interface Props {
  /** Initial markdown — loaded into the editor on mount. */
  initialMarkdown: string;
  /** Save handler. Receives the round-tripped markdown. */
  onSave: (markdown: string) => Promise<void>;
  onCancel: () => void;
  saving?: boolean;
}

/**
 * TipTap-based WYSIWYG editor for `workflowBriefs.markdown` (OAIP-76).
 *
 * Markdown round-trip:
 *   - On mount, `tiptap-markdown` parses `initialMarkdown` into the editor's
 *     ProseMirror document.
 *   - On Save, `editor.storage.markdown.getMarkdown()` re-serializes the
 *     document back to markdown so the persisted format stays compatible
 *     with the existing react-markdown renderer + jspdf/html2canvas PDF
 *     export.
 *
 * Toolbar covers the formatting OAIP staff actually use in briefs (bold,
 * italic, headings, lists, links, blockquote). Anything more elaborate
 * stays as raw markdown — TipTap displays it untouched and round-trips
 * it cleanly because the markdown extension preserves unknown nodes.
 */
export default function BriefEditor({ initialMarkdown, onSave, onCancel, saving }: Props) {
  const { message } = App.useApp();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // We opt into the markdown extension's link instead of StarterKit's
        // default so we get a configurable URL prompt.
        link: false,
      } as any),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      Markdown.configure({
        html: false,
        tightLists: true,
        bulletListMarker: "-",
        linkify: true,
        breaks: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: initialMarkdown,
  });

  // Keep the editor mirrored to incoming initial markdown if it changes
  // (e.g., a different brief was selected in the rail while editing).
  useEffect(() => {
    if (!editor) return;
    const current = (editor.storage as any).markdown?.getMarkdown?.() ?? "";
    if (current.trim() !== initialMarkdown.trim()) {
      editor.commands.setContent(initialMarkdown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMarkdown, editor]);

  const handleSave = async () => {
    if (!editor) return;
    const markdown = (editor.storage as any).markdown?.getMarkdown?.() ?? "";
    if (!markdown.trim()) {
      message.warning("Brief content is empty.");
      return;
    }
    await onSave(markdown);
  };

  const handleAddLink = () => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) return null;

  const toolbarBtn = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    active = false,
  ) => (
    <Tooltip title={label}>
      <Button
        size="small"
        type={active ? "primary" : "text"}
        icon={icon}
        onClick={onClick}
        aria-label={label}
        aria-pressed={active}
      />
    </Tooltip>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap size={4} style={{ background: "#fafafa", padding: 8, borderRadius: 4 }}>
        {toolbarBtn(
          <BoldOutlined />,
          "Bold (⌘B)",
          () => editor.chain().focus().toggleBold().run(),
          editor.isActive("bold"),
        )}
        {toolbarBtn(
          <ItalicOutlined />,
          "Italic (⌘I)",
          () => editor.chain().focus().toggleItalic().run(),
          editor.isActive("italic"),
        )}
        <Divider type="vertical" />
        {toolbarBtn(
          <span style={{ fontWeight: 700 }}>H1</span>,
          "Heading 1",
          () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          editor.isActive("heading", { level: 1 }),
        )}
        {toolbarBtn(
          <span style={{ fontWeight: 700 }}>H2</span>,
          "Heading 2",
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          editor.isActive("heading", { level: 2 }),
        )}
        {toolbarBtn(
          <span style={{ fontWeight: 700 }}>H3</span>,
          "Heading 3",
          () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          editor.isActive("heading", { level: 3 }),
        )}
        <Divider type="vertical" />
        {toolbarBtn(
          <UnorderedListOutlined />,
          "Bulleted list",
          () => editor.chain().focus().toggleBulletList().run(),
          editor.isActive("bulletList"),
        )}
        {toolbarBtn(
          <OrderedListOutlined />,
          "Numbered list",
          () => editor.chain().focus().toggleOrderedList().run(),
          editor.isActive("orderedList"),
        )}
        {toolbarBtn(
          <span>"</span>,
          "Quote",
          () => editor.chain().focus().toggleBlockquote().run(),
          editor.isActive("blockquote"),
        )}
        <Divider type="vertical" />
        {toolbarBtn(<LinkOutlined />, "Link", handleAddLink, editor.isActive("link"))}
      </Space>

      <div
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          padding: "12px 16px",
          minHeight: 320,
        }}
      >
        <EditorContent editor={editor} />
      </div>

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSave} loading={saving}>
          Save brief
        </Button>
      </Space>
    </Space>
  );
}
